import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  CreateMappingElementMutationRequest,
  CreateMappingElementMutationResponse,
} from "../types/CreateMappingElement"

type CreateMappingElementClient = typeof client<
  CreateMappingElementMutationResponse,
  Error,
  CreateMappingElementMutationRequest
>
type CreateMappingElement = {
  data: CreateMappingElementMutationResponse
  error: Error
  request: CreateMappingElementMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<CreateMappingElementClient>>
  client: {
    parameters: Partial<Parameters<CreateMappingElementClient>[0]>
    return: Awaited<ReturnType<CreateMappingElementClient>>
  }
}
/**
 * @summary Create a mapping element
 * @link /v1/client-settings/create-mapping-element
 */
export function useCreateMappingElement(
  options: {
    mutation?: UseMutationOptions<
      CreateMappingElement["response"],
      CreateMappingElement["error"],
      CreateMappingElement["request"]
    >
    client?: CreateMappingElement["client"]["parameters"]
  } = {}
): UseMutationResult<
  CreateMappingElement["response"],
  CreateMappingElement["error"],
  CreateMappingElement["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    CreateMappingElement["response"],
    CreateMappingElement["error"],
    CreateMappingElement["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        CreateMappingElement["data"],
        CreateMappingElement["error"],
        CreateMappingElement["request"]
      >({
        method: "post",
        url: `/v1/client-settings/create-mapping-element`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
